import { useEffect, useState } from 'react';
import {
	Box,
	Chip,
	DialogContent,
	Divider,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Switch,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	alpha,
	useTheme,
} from '@mui/material';
import {
	AccountBalanceWalletOutlined,
	CalendarMonthOutlined,
	DescriptionOutlined,
	ReceiptLongOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from './MoleDialogShell';
import { FilterDateRange, formatPeriodLabel } from '../hooks/useDateRange';
import PageOrientationCards from './PageOrientationCards';
import type { PageOrientation } from './PageOrientationCards';

export type { PageOrientation } from './PageOrientationCards';

export type SituationExportMode = 'client' | 'agent';
export type SituationReportKind = 'ledger' | 'debt';

export interface SituationDebtExportOptions {
	fileType: 'pdf' | 'csv';
	includeZeroBalance: boolean;
	includeSuspended: boolean;
	comprehensive: boolean;
}

interface Props {
	open: boolean;
	onClose: () => void;
	mode: SituationExportMode;
	period: FilterDateRange;
	selectedIds: number[];
	onExportLedger: (selectedIds: number[], orientation: PageOrientation) => void;
	onExportDebt: (selectedIds: number[], options: SituationDebtExportOptions) => void;
}

const SituationExportDialog = ({
	open,
	onClose,
	mode,
	period,
	selectedIds,
	onExportLedger,
	onExportDebt,
}: Props) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const themeColor = useMoleThemeColor();
	const [reportKind, setReportKind] = useState<SituationReportKind>('ledger');
	const [orientation, setOrientation] = useState<PageOrientation>('landscape');
	const [fileType, setFileType] = useState<'pdf' | 'csv'>('pdf');
	const [includeZeroBalance, setIncludeZeroBalance] = useState(true);
	const [includeSuspended, setIncludeSuspended] = useState(true);
	const [comprehensive, setComprehensive] = useState(false);

	const periodLabel = formatPeriodLabel(period) ?? t('common:clientSituation.export.currentSnapshot');
	const entityLabel = mode === 'client'
		? t('common:tables.client')
		: t('common:tables.agent');
	const scopeLabel = selectedIds.length > 0
		? t('common:clientSituation.export.selectedCount', { count: selectedIds.length, entity: entityLabel })
		: t('common:clientSituation.export.allEntities', { entity: entityLabel });

	useEffect(() => {
		if (!open) return;
		setReportKind('ledger');
		setOrientation('landscape');
		setFileType('pdf');
		setIncludeZeroBalance(true);
		setIncludeSuspended(true);
		setComprehensive(false);
	}, [open]);

	const handleExport = () => {
		if (reportKind === 'ledger') {
			onExportLedger(selectedIds, orientation);
		} else {
			onExportDebt(selectedIds, {
				fileType,
				includeZeroBalance,
				includeSuspended,
				comprehensive,
			});
		}
		onClose();
	};

	const optionCardSx = (selected: boolean, accent: string) => ({
		flex: 1,
		p: 2.5,
		borderRadius: 2.5,
		border: '2px solid',
		borderColor: selected ? accent : 'divider',
		bgcolor: selected ? alpha(accent, 0.08) : 'background.paper',
		cursor: 'pointer',
		position: 'relative' as const,
		transition: 'all 0.2s ease',
		'&:hover': {
			borderColor: accent,
			bgcolor: alpha(accent, 0.06),
			transform: 'translateY(-2px)',
			boxShadow: theme.shadows[4],
		},
	});

	return (
		<MoleDialog open={open} onClose={onClose} maxWidth="md" fullWidth themeColor={themeColor}>
			<MoleDialogHeader
				title={t('common:clientSituation.export.title')}
				subtitle={t(`common:clientSituation.export.subtitle.${mode}`)}
				icon={<DescriptionOutlined />}
				onClose={onClose}
				themeColor={themeColor}
			/>

			<DialogContent sx={{ pt: 1 }}>
				<Stack spacing={2.5} sx={{ mt: 0.5 }}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							px: 1.5,
							py: 1,
							borderRadius: 2,
							bgcolor: alpha(theme.palette.primary.main, 0.06),
							border: '1px solid',
							borderColor: alpha(theme.palette.primary.main, 0.15),
						}}
					>
						<CalendarMonthOutlined fontSize="small" color="primary" />
						<Box>
							<Typography variant="caption" color="text.secondary" display="block">
								{t('common:ui.labels.period')}
							</Typography>
							<Typography variant="body2" fontWeight={600}>
								{periodLabel}
							</Typography>
						</Box>
					</Box>

					<Chip
						label={scopeLabel}
						size="small"
						color={selectedIds.length > 0 ? 'primary' : 'default'}
						variant={selectedIds.length > 0 ? 'filled' : 'outlined'}
						sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
					/>

					<Box>
						<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
							{t('common:clientSituation.export.chooseReport')}
						</Typography>
						<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
							<Box
								onClick={() => setReportKind('ledger')}
								sx={optionCardSx(reportKind === 'ledger', theme.palette.primary.main)}
							>
								<ReceiptLongOutlined sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
								<Typography variant="subtitle1" fontWeight={700}>
									{t('common:clientSituation.export.ledger.title')}
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
									{t('common:clientSituation.export.ledger.description')}
								</Typography>
							</Box>
							<Box
								onClick={() => setReportKind('debt')}
								sx={optionCardSx(reportKind === 'debt', theme.palette.success.main)}
							>
								<AccountBalanceWalletOutlined sx={{ fontSize: 36, color: 'success.main', mb: 1 }} />
								<Typography variant="subtitle1" fontWeight={700}>
									{t(`common:clientSituation.export.debt.title${mode === 'client' ? 'Client' : 'Agent'}`)}
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
									{t('common:clientSituation.export.debt.description')}
								</Typography>
							</Box>
						</Stack>
					</Box>

					{reportKind === 'ledger' && (
						<PageOrientationCards value={orientation} onChange={setOrientation} />
					)}

					{reportKind === 'debt' && (
						<>
							<Divider />

							<FormControl fullWidth size="small">
								<InputLabel id="situation-file-type-label">
									{t('common:ui.labels.fileType')}
								</InputLabel>
								<Select
									labelId="situation-file-type-label"
									value={fileType}
									label={t('common:ui.labels.fileType')}
									onChange={(e) => setFileType(e.target.value as 'pdf' | 'csv')}
								>
									<MenuItem value="pdf">{t('common:export.fileTypes.pdf')}</MenuItem>
									<MenuItem value="csv">{t('common:export.fileTypes.csv')}</MenuItem>
								</Select>
							</FormControl>

							<Box>
								<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
									{t('common:clientSituation.export.debt.detailLevel')}
								</Typography>
								<ToggleButtonGroup
									exclusive
									fullWidth
									size="small"
									value={comprehensive ? 'comprehensive' : 'simple'}
									onChange={(_, value) => {
										if (value) setComprehensive(value === 'comprehensive');
									}}
								>
									<ToggleButton value="simple">
										{t('common:clientSituation.export.debt.simple')}
									</ToggleButton>
									<ToggleButton value="comprehensive">
										{t('common:clientSituation.export.debt.comprehensive')}
									</ToggleButton>
								</ToggleButtonGroup>
								<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
									{comprehensive
										? t('common:clientSituation.export.debt.comprehensiveHint')
										: t('common:clientSituation.export.debt.simpleHint')}
								</Typography>
							</Box>

							<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
								<FormControlLabel
									control={
										<Switch
											checked={includeZeroBalance}
											onChange={(e) => setIncludeZeroBalance(e.target.checked)}
											color="primary"
										/>
									}
									label={t('common:export.showZeroBalance')}
									sx={{ flex: 1 }}
								/>
								<FormControlLabel
									control={
										<Switch
											checked={includeSuspended}
											onChange={(e) => setIncludeSuspended(e.target.checked)}
											color="primary"
										/>
									}
									label={t('common:export.showSuspended')}
									sx={{ flex: 1 }}
								/>
							</Stack>
						</>
					)}
				</Stack>
			</DialogContent>

			<MoleDialogActions>
				<MoleCancelButton onClick={onClose}>{t('common:ui.buttons.cancel')}</MoleCancelButton>
				<MolePrimaryButton onClick={handleExport} startIcon={<DescriptionOutlined />}>
					{t('common:ui.buttons.export')}
				</MolePrimaryButton>
			</MoleDialogActions>
		</MoleDialog>
	);
};

export default SituationExportDialog;